import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useDebounce } from "@/hooks/use-debounce";
import type { Member, MemberClassification } from "@/types/syra";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { Search, User, Star, Zap, ChevronLeft, ChevronRight, RefreshCw, Users } from "lucide-react";
import { toast } from "sonner";
import { Progress } from "@/components/ui/progress";
import { motion } from "framer-motion";
import { MemberAdminToggle } from "@/components/members/MemberAdminToggle";

function formatPhone(phone: string): string {
  if (!phone || phone.startsWith("pending_")) return "Aguardando...";
  const match = phone.match(/^(55)(\d{2})(\d{4,5})(\d{4})$/);
  if (match) return `+${match[1]} (${match[2]}) ${match[3]}-${match[4]}`;
  if (/^\d{10,15}$/.test(phone)) {
    if (phone.length <= 13) return `+${phone}`;
    return "ID interno";
  }
  return phone;
}

function normalizeName(name: string): string {
  return name.trim().toLowerCase().replace(/\s+/g, " ");
}

function isResolvedPhone(phone: string): boolean {
  return /^\d{10,13}$/.test(phone);
}

const classificationColors: Record<MemberClassification, string> = {
  contribuidor_ativo: "bg-success/15 text-success border-success/30",
  participante: "bg-info/15 text-info border-info/30",
  observador: "bg-warning/15 text-warning border-warning/30",
  inativo: "bg-muted text-muted-foreground border-border",
};

const classificationLabels: Record<MemberClassification, string> = {
  contribuidor_ativo: "Contribuidor Ativo",
  participante: "Participante",
  observador: "Observador",
  inativo: "Inativo",
};

interface GroupedMember {
  phone: string;
  name: string;
  groups: string[];
  classification: MemberClassification;
  engagement_score: number;
  expertises: string[];
  suggested_connections: string[];
  last_activity: string | null;
  entries: Member[];
}

const PAGE_SIZE = 20;
const FETCH_BATCH_SIZE = 1000;

export default function Members() {
  const [search, setSearch] = useState("");
  const [classFilter, setClassFilter] = useState<string>("all");
  const [groupFilter, setGroupFilter] = useState<string>("all");
  const [selectedMember, setSelectedMember] = useState<GroupedMember | null>(null);
  const [page, setPage] = useState(1);
  const [syncing, setSyncing] = useState(false);
  const debouncedSearch = useDebounce(search);

  const { data: groups = [] } = useQuery({
    queryKey: ["member-groups"],
    queryFn: async () => {
      const { data } = await supabase.from("syra_groups").select("id, name").eq("active", true).order("name");
      return (data ?? []) as { id: string; name: string }[];
    },
  });

  const { data: allMembers = [], isLoading, refetch } = useQuery({
    queryKey: ["members", classFilter, groupFilter, debouncedSearch],
    queryFn: async () => {
      const rows: Member[] = [];
      let from = 0;
      while (true) {
        let query = supabase.from("syra_members").select("*").order("engagement_score", { ascending: false }).order("created_at", { ascending: false }).range(from, from + FETCH_BATCH_SIZE - 1);
        if (classFilter && classFilter !== "all") query = query.eq("classification", classFilter as any);
        if (groupFilter && groupFilter !== "all") query = query.eq("group_id", groupFilter as any);
        if (debouncedSearch) query = query.ilike("name", `%${debouncedSearch}%`);
        const { data, error } = await query;
        if (error) throw new Error(error.message);
        const batch = (data ?? []) as Member[];
        rows.push(...batch);
        if (batch.length < FETCH_BATCH_SIZE) break;
        from += FETCH_BATCH_SIZE;
      }
      return rows;
    },
  });

  const resolvedPhoneByName = useMemo(() => {
    const candidates = new Map<string, Set<string>>();
    for (const member of allMembers) {
      if (!isResolvedPhone(member.phone)) continue;
      const normalizedName = normalizeName(member.name);
      if (!normalizedName) continue;
      const phones = candidates.get(normalizedName) ?? new Set<string>();
      phones.add(member.phone);
      candidates.set(normalizedName, phones);
    }
    const resolved = new Map<string, string>();
    for (const [name, phones] of candidates.entries()) {
      if (phones.size === 1) resolved.set(name, Array.from(phones)[0]);
    }
    return resolved;
  }, [allMembers]);

  const grouped = useMemo<GroupedMember[]>(() => {
    const map = new Map<string, GroupedMember>();
    for (const m of allMembers) {
      const normalizedName = normalizeName(m.name);
      const resolvedPhone = isResolvedPhone(m.phone) ? m.phone : resolvedPhoneByName.get(normalizedName) ?? m.phone;
      const key = isResolvedPhone(resolvedPhone) ? resolvedPhone : `pending:${m.group_id ?? "unknown"}:${normalizedName || m.id}`;
      const existing = map.get(key);
      if (existing) {
        if (m.group_name && !existing.groups.includes(m.group_name)) existing.groups.push(m.group_name);
        if (!isResolvedPhone(existing.phone) && isResolvedPhone(resolvedPhone)) existing.phone = resolvedPhone;
        if (m.engagement_score > existing.engagement_score) { existing.engagement_score = m.engagement_score; existing.classification = m.classification; existing.name = m.name; }
        if (m.last_activity && (!existing.last_activity || m.last_activity > existing.last_activity)) existing.last_activity = m.last_activity;
        existing.expertises = [...new Set([...existing.expertises, ...m.expertises])];
        existing.suggested_connections = [...new Set([...existing.suggested_connections, ...m.suggested_connections])];
        existing.entries.push(m as Member);
      } else {
        map.set(key, { phone: resolvedPhone, name: m.name, groups: m.group_name ? [m.group_name] : [], classification: m.classification, engagement_score: m.engagement_score, expertises: [...m.expertises], suggested_connections: [...m.suggested_connections], last_activity: m.last_activity ?? null, entries: [m as Member] });
      }
    }
    return Array.from(map.values()).sort((a, b) => {
      if (b.engagement_score !== a.engagement_score) return b.engagement_score - a.engagement_score;
      return (b.last_activity ? new Date(b.last_activity).getTime() : 0) - (a.last_activity ? new Date(a.last_activity).getTime() : 0);
    });
  }, [allMembers, resolvedPhoneByName]);

  const total = grouped.length;
  const totalPages = Math.ceil(total / PAGE_SIZE);
  const members = grouped.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const { data, error } = await supabase.functions.invoke("sync-members");
      if (error) throw error;
      toast.success(`Sincronização concluída! ${data?.synced ?? 0} participantes processados.`);
      if (data?.errors?.length) data.errors.forEach((e: string) => toast.warning(e));
      refetch();
    } catch (err: any) {
      toast.error("Erro na sincronização: " + (err?.message || "desconhecido"));
    } finally { setSyncing(false); }
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground font-headline tracking-tight">CRM de Membros</h1>
          <p className="text-muted-foreground mt-1">
            {total > 0 ? `${total} membros únicos` : "Gerencie seus participantes"}
          </p>
        </div>
        <Button onClick={handleSync} disabled={syncing} variant="outline" size="sm" className="shadow-sm">
          <RefreshCw className={`w-4 h-4 mr-1 ${syncing ? "animate-spin" : ""}`} />
          {syncing ? "Sincronizando..." : "Sincronizar"}
        </Button>
      </div>

      {syncing && <Progress value={undefined} className="h-1" />}

      {/* Filters */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
        className="flex flex-col sm:flex-row gap-3"
      >
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input placeholder="Buscar por nome..." value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} className="pl-9" />
        </div>
        <Select value={classFilter} onValueChange={(v) => { setClassFilter(v); setPage(1); }}>
          <SelectTrigger className="w-[200px]"><SelectValue placeholder="Classificação" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas</SelectItem>
            <SelectItem value="contribuidor_ativo">Contribuidor Ativo</SelectItem>
            <SelectItem value="participante">Participante</SelectItem>
            <SelectItem value="observador">Observador</SelectItem>
            <SelectItem value="inativo">Inativo</SelectItem>
          </SelectContent>
        </Select>
        <Select value={groupFilter} onValueChange={(v) => { setGroupFilter(v); setPage(1); }}>
          <SelectTrigger className="w-[220px]"><SelectValue placeholder="Grupo" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os Grupos</SelectItem>
            {groups.map((g) => (
              <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </motion.div>

      {/* Table */}
      {isLoading ? (
        <Card className="border-border/50 shadow-sm">
          <CardContent className="p-4 space-y-3">
            {[1, 2, 3, 4, 5].map((i) => <Skeleton key={i} className="h-12 w-full rounded-md" />)}
          </CardContent>
        </Card>
      ) : (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
        >
          <Card className="border-border/50 shadow-sm hover:shadow-md transition-shadow duration-300">
            <CardContent className="p-0 overflow-x-auto">
              <Table className="min-w-[700px]">
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    <TableHead className="font-semibold">Nome</TableHead>
                    <TableHead className="font-semibold">Telefone</TableHead>
                    <TableHead className="font-semibold">Grupos</TableHead>
                    <TableHead className="font-semibold">Classificação</TableHead>
                    <TableHead className="font-semibold">Engajamento</TableHead>
                    <TableHead className="font-semibold">Última Atividade</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {members.map((m, index) => (
                    <motion.tr
                      key={`${m.phone}-${m.name}`}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 0.3, delay: index * 0.02 }}
                      className="cursor-pointer border-b transition-colors hover:bg-accent/50 data-[state=selected]:bg-muted"
                      onClick={() => setSelectedMember(m)}
                    >
                      <TableCell className="font-medium">{m.name}</TableCell>
                      <TableCell className="text-muted-foreground font-mono text-xs">{formatPhone(m.phone)}</TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {m.groups.map((g) => (
                            <Badge key={g} variant="outline" className="text-xs bg-primary/5 border-primary/20">{g}</Badge>
                          ))}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={classificationColors[m.classification]}>
                          {classificationLabels[m.classification]}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1.5">
                          <div className="w-16 h-1.5 bg-muted rounded-full overflow-hidden">
                            <div className="h-full bg-warning rounded-full" style={{ width: `${m.engagement_score}%` }} />
                          </div>
                          <span className="text-xs text-muted-foreground font-medium">{m.engagement_score}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {m.last_activity ? new Date(m.last_activity).toLocaleDateString("pt-BR") : "-"}
                      </TableCell>
                    </motion.tr>
                  ))}
                  {members.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-16">
                        <Users className="w-12 h-12 mx-auto mb-3 text-muted-foreground/30" />
                        <p className="text-muted-foreground text-sm">Nenhum membro encontrado</p>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-3 mt-6">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)} className="shadow-sm">
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-sm text-muted-foreground font-medium">
                Página {page} de {totalPages}
              </span>
              <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(page + 1)} className="shadow-sm">
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          )}
        </motion.div>
      )}

      {/* Member Detail Dialog */}
      <Dialog open={!!selectedMember} onOpenChange={() => setSelectedMember(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-primary/15 text-primary flex items-center justify-center">
                <User className="w-4 h-4" />
              </div>
              {selectedMember?.name}
            </DialogTitle>
          </DialogHeader>
          {selectedMember && <MemberProfileGrouped member={selectedMember} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function MemberProfileGrouped({ member }: { member: GroupedMember }) {
  const adminEntries = member.entries.map((e) => ({
    id: e.id,
    group_id: e.group_id ?? null,
    group_name: e.group_name ?? null,
    is_group_admin: (e as any).is_group_admin ?? false,
  }));

  return (
    <div className="space-y-4">
      <MemberAdminToggle entries={adminEntries} memberName={member.name} />

      <div className="grid grid-cols-2 gap-3 text-sm">
        <div>
          <p className="text-muted-foreground text-xs uppercase tracking-wider mb-1">Telefone</p>
          <p className="font-medium text-foreground font-mono">{formatPhone(member.phone)}</p>
        </div>
        <div>
          <p className="text-muted-foreground text-xs uppercase tracking-wider mb-1">Grupos</p>
          <div className="flex flex-wrap gap-1 mt-1">
            {member.groups.map((g) => (
              <Badge key={g} variant="outline" className="text-xs bg-primary/5 border-primary/20">{g}</Badge>
            ))}
          </div>
        </div>
        <div>
          <p className="text-muted-foreground text-xs uppercase tracking-wider mb-1">Classificação</p>
          <Badge variant="outline" className={classificationColors[member.classification]}>
            {classificationLabels[member.classification]}
          </Badge>
        </div>
        <div>
          <p className="text-muted-foreground text-xs uppercase tracking-wider mb-1">Engajamento</p>
          <div className="flex items-center gap-2">
            <div className="w-full h-1.5 bg-muted rounded-full overflow-hidden">
              <div className="h-full bg-warning rounded-full" style={{ width: `${member.engagement_score}%` }} />
            </div>
            <span className="text-xs font-medium">{member.engagement_score}</span>
          </div>
        </div>
      </div>

      {member.expertises.length > 0 && (
        <div>
          <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Expertises Detectadas</p>
          <div className="flex flex-wrap gap-1">
            {member.expertises.map((e) => (
              <Badge key={e} variant="secondary" className="text-xs">{e}</Badge>
            ))}
          </div>
        </div>
      )}

      {member.suggested_connections.length > 0 && (
        <div>
          <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1">
            <Zap className="w-3 h-3" /> Conexões Sugeridas
          </p>
          <div className="flex flex-wrap gap-1">
            {member.suggested_connections.map((c) => (
              <Badge key={c} variant="outline" className="text-xs">{c}</Badge>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
